import { CurrencyView } from '../currency-view';
import { BlockLoading } from '../fetch-status/block-loading';
import { Button } from '../ui/button';
import { ClaimButton } from './claim-button';
import { CTF_ADDRESS } from '@/client/confs/app';
import { useOnChainPosition } from '@/client/hooks/use-on-chain';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { ClaimStatus, MarketStatus } from '@/libs/sdk/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Address } from 'viem';
import { useLocation } from 'wouter';

type ViewDetailsProps = {
  eventId?: string;
  marketAddress: string;
};
export const ViewDetails = ({ eventId, marketAddress }: ViewDetailsProps) => {
  const { t } = useTranslation();
  const [location, navigate] = useLocation();
  const isMarketPage = useMemo(() => location.startsWith('/market') || location.startsWith('/event'), [location]);
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  if (isMarketPage) return null;
  return (
    <Button
      variant="default"
      size="lg"
      onClick={() => {
        navigate(eventId ? `/event/${eventId}` : `/market/${marketAddress}`);
        setTradeOpen(false);
      }}
      className="mt-4 bg-foreground/10 p-4 text-center hover:text-white text-sm  rounded-3 font-semibold leading-none w-full"
    >
      {t('view-details')}
    </Button>
  );
};

type UserEaringsProps = {
  className?: string;
  eventId?: string;
  marketAddress: string;
  optionAddress: string;
  marketStatus: MarketStatus;
  isClaim: ClaimStatus;
  optionsLen: number;
  uid: string;
};
export const UserEarings = ({
  className,
  eventId,
  marketAddress,
  marketStatus,
  optionAddress,
  optionsLen,
  isClaim,
  uid,
}: UserEaringsProps) => {
  const { t } = useTranslation();

  // On-chain ERC-1155 balance check for claim status
  const { data: userInfo } = useSignedUserInfo(api);
  const walletAddress = userInfo?.eoaAddress as Address | undefined;
  const market = useMarketDetail(api, { marketAddress }, { subscribe: false });
  const winningPositionId = useMemo(() => {
    if (!optionAddress || !market.data?.optionList) return undefined;
    const option = market.data.optionList.find(item => item.optionAddress.toLowerCase() === optionAddress.toLowerCase());
    return option?.positionId ? BigInt(option.positionId) : undefined;
  }, [optionAddress, market.data?.optionList]);

  const onChainPosition = useOnChainPosition(CTF_ADDRESS as Address | undefined, walletAddress, winningPositionId, {
    marketStatus,
  });

  // On-chain claim status: balance == 0 means tokens burned (claimed)
  // Takes priority over DB isClaim; falls back to DB when on-chain data unavailable
  const isClaimedOnChain = useMemo(() => {
    if (marketStatus !== MarketStatus.closed) return undefined; // only check for resolved markets
    if (onChainPosition.data === undefined) return undefined; // still loading
    return onChainPosition.data === 0n;
  }, [marketStatus, onChainPosition.data]);

  const params = useMemo(() => {
    return {
      page: 1,
      pageSize: optionsLen,
      marketAddress: marketAddress,
      uid,
    };
  }, [marketAddress, optionsLen, uid]);
  const userPositions = useUserPositions(api, params, {
    enabled: marketStatus === MarketStatus.closed,
  });
  const positions = useMemo(() => {
    const raw = userPositions.data?.pages.flatMap(item => item.positions) || [];
    // Override prices with settlement values when market is resolved
    if (marketStatus >= MarketStatus.closed && optionAddress) {
      return raw.map(p => {
        const isWinner = p.optionAddress.toLowerCase() === optionAddress.toLowerCase();
        const scale = Math.pow(10, p.optionDecimal);
        const settlementPrice = isWinner ? String(scale) : '0';
        const balance = Number(p.toWin) || 0;
        const settlementValue = isWinner ? String(balance) : '0';
        return { ...p, marketPirce: settlementPrice, currentValue: settlementValue };
      });
    }
    return raw;
  }, [userPositions.data?.pages, marketStatus, optionAddress]);
  if (marketStatus < MarketStatus.settlement) return null;
  if (userPositions.isLoading) return <BlockLoading />;
  if (positions.length === 0 || marketStatus <= MarketStatus.disputed) {
    return <ViewDetails eventId={eventId} marketAddress={marketAddress} />;
  }
  // Determine claimed: on-chain balance takes priority, DB isClaim as fallback
  const hasClaimed = isClaimedOnChain ?? isClaim === ClaimStatus.yes;
  if (hasClaimed) {
    return (
      <>
        <p className="text-sm text-beneficial text-center py-2">✓ 已領取收益</p>
        <ViewDetails eventId={eventId} marketAddress={marketAddress} />
      </>
    );
  }
  return (
    <div className={cn('space-y-2', className)}>
      <div className="space-y-2 max-h-95 overflow-y-auto">
        {positions.map(item => (
          <div
            key={`user-earings-${item.optionAddress}`}
            className="bg-foreground/4 p-4 rounded-3 text-sm  leading-[normal] space-y-3"
          >
            <h3 className="font-semibold text-foreground">{t('userearings')}</h3>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('option')}</span>
              <span className="font-semibold">{item.optionDescription ?? item.optionName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('position')}</span>
              <span className="font-semibold">
                <CurrencyView value={item.toWin} decimal={item.optionDecimal} />
                {t('shares')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('value-per-share')}</span>
              <CurrencyView
                className="font-semibold inline-flex items-center"
                showIcon={true}
                baseTokenAddress={market.data?.baseTokenAddress}
                value={item.marketPirce}
                decimal={item.optionDecimal}
                precision={2}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('total')}</span>
              <CurrencyView
                className="font-semibold inline-flex items-center"
                showIcon={true}
                baseTokenAddress={market.data?.baseTokenAddress}
                value={item.currentValue}
                decimal={item.optionDecimal}
                precision={2}
              />
            </div>
          </div>
        ))}
      </div>
      <ClaimButton marketAddress={marketAddress} optionAddress={optionAddress} />
    </div>
  );
};
